'use client';

import type { SingleProductFreeOrderItem } from '../../../../api/free-order/dto/free-order-item.interface';
import useIsBabyfoodProduct from '../../../../app/ustore/babyfood/hooks/useIsBabyfoodProduct';
import UImage from '../../../../components/UImage';
import { AgeRestrictedPolicy } from '../../../../policy/AgeRestrictedPolicy';
import { RecommendationLabel } from '../../../../type/ustore/recommendation-label.enum';
import { getDiscountPercent } from '../../../../utils/common';
import { defaultItemThumbnail } from '../../../../utils/common/variables';
import useClickFreeOrderCard from '../../../../utils/hook/ustore/useClickFreeOrderCard';
import { UstoreFreeOrderCardPolicy } from '../../../../utils/policy/UstoreFreeOrderCardPolicy';
import { UstorePolicy } from '../../../../utils/policy/UstorePolicy';
import UstoreFreeOrderCard from '../UstoreFreeOrderCard';

/** 썸네일 이미지 폭 — 카드가 가장 넓어지는 시트 최대 폭(480px) 기준 자리 크기 */
const THUMBNAIL_SIZE_PX = 180;

interface AlsoBoughtProductCardProps {
  product: SingleProductFreeOrderItem;
  /** 가로 스크롤 안에서의 순서 — 이벤트 position 값 */
  index: number;
  /** 구좌 이름 — 카드 클릭 이벤트의 curation 값 */
  curation: string;
  /** 상세로 이동한 뒤 호출 — 시트를 닫아 다음 화면 위에 남지 않게 한다 */
  onNavigate: () => void;
}

/**
 * 담기 완료 바텀시트의 함께 구매한 상품 카드
 *
 * 커스터마이징 추천 카드와 같은 모양이되 담기 버튼은 두지 않는다.
 * 시트 위에 또 시트를 띄우지 않고, 고가 상품은 상세를 읽고 사는 흐름이라
 * 카드를 누르면 상품 상세로 보낸다.
 * 썸네일 위 태그(19세 이상·큐레이션 플래그·무료배송)는 다른 마켓 카드와 같은 정책을 따른다.
 */
const AlsoBoughtProductCard = ({
  product,
  index,
  curation,
  onNavigate,
}: AlsoBoughtProductCardProps) => {
  const { handleClick } = useClickFreeOrderCard();
  const { checkIsBabyFoodProduct } = useIsBabyfoodProduct();

  const salePrice = UstorePolicy.getDisplaySalePrice({
    thumbnailSalePrice: product.thumbnailSalePrice,
    salePrice: product.salePrice,
  });
  const discountPercent = getDiscountPercent(salePrice, product.originalPrice);

  const overlayResult = UstoreFreeOrderCardPolicy.getOverlayType({
    item: product,
    // 못난이 특가 인증사진 오버레이는 이 구좌에서 쓰지 않는다
    checkPointContents: [],
    isAgeRestricted: AgeRestrictedPolicy.isAgeRestrictedProduct({
      category: product.category,
    }),
    isBabyFoodProduct: checkIsBabyFoodProduct(product),
  });

  const handleClickCard = () => {
    handleClick({
      item: product,
      index,
      curation,
      tab_name: null,
      // 상세 주소에도 함께 실려 넘어가, 상세에서 이어지는 담기까지 이 구좌 성과로 잡힌다
      is_recommendation: true,
      recommendation_label: RecommendationLabel.BOUGHT_TOGETHER,
    });
    onNavigate();
  };

  return (
    // 썸네일 위 태그가 div라 button 안에 넣을 수 없어, 상세 추천 구좌와 같이 div로 받는다
    <div
      onClick={handleClickCard}
      className="flex w-full cursor-pointer flex-col gap-[8px] text-left"
    >
      <UstoreFreeOrderCard.Container className="aspect-[1/1]">
        <UImage
          src={product.thumbnail || defaultItemThumbnail}
          width={THUMBNAIL_SIZE_PX}
          height={THUMBNAIL_SIZE_PX}
          alt={product.name}
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <UstoreFreeOrderCard.ThumbnailOverlays overlayResult={overlayResult} />
      </UstoreFreeOrderCard.Container>

      <div className="flex flex-col gap-[6px]">
        <p className="line-clamp-2 text-[14px] font-medium leading-[1.4] text-gray-800">
          {product.name}
        </p>

        <p className="flex items-center gap-[4px] text-[12px] font-bold leading-[1]">
          {discountPercent > 0 && (
            <span className="text-brand-1">{discountPercent}%</span>
          )}
          <span className="text-gray-800">{salePrice.toLocaleString()}원</span>
        </p>
      </div>
    </div>
  );
};

export default AlsoBoughtProductCard;
